import React, { useEffect, useRef, useState } from "react";
import { useQuery } from "@apollo/client/react";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import LinearProgress from "@mui/material/LinearProgress";
import Pagination from "@mui/material/Pagination";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useLocation, useNavigate } from "react-router";

import { LOCATION_TIMELINE_QUERY } from "../../../../constants/graphql/queries";

const PAGE_LIMIT = 25;
const LOCATION_ACTIVITY_PARAM = "locationActivity";

const ACTION_COLORS = {
  asset_entered: "#D5F5D5",
  asset_left: "#F8D7DA",
  mobile_location_move: "#CCE7FF",
  inter_warehouse_move: "#FFE0B2",
};

const crossesWarehouses = (event) =>
  Boolean(
    event.fromLocation?.warehouse?.id &&
      event.toLocation?.warehouse?.id &&
      event.fromLocation.warehouse.id !== event.toLocation.warehouse.id,
  );

const movedItemsCount = (event) => {
  if (!event.occupiedAtTime) return 0;
  return Array.isArray(event.assetIds) ? event.assetIds.length : 0;
};

const ActionPill = ({ label, color }) => (
  <span
    style={{
      display: "inline-block",
      backgroundColor: color,
      color: "#000",
      padding: "4px 12px",
      borderRadius: "4px",
      fontSize: "12px",
      fontWeight: 450,
      textAlign: "center",
      lineHeight: "1.4",
      minWidth: "64px",
      maxWidth: "90px",
    }}
  >
    {label}
  </span>
);

const formatDate = (value) => {
  if (!value) return { date: "—", time: "" };
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return { date: value, time: "" };

  return {
    date: date.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    }),
    time: date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }),
  };
};

const EventLabel = ({ event }) => {
  if (event.eventType === "asset_entered") {
    return <ActionPill label="Entry" color={ACTION_COLORS.asset_entered} />;
  }
  if (event.eventType === "asset_left") {
    return <ActionPill label="Exit" color={ACTION_COLORS.asset_left} />;
  }

  const inter = crossesWarehouses(event);

  return (
    <ActionPill
      label={inter ? "Warehouse Transfer" : "Location Move"}
      color={inter ? ACTION_COLORS.inter_warehouse_move : ACTION_COLORS.mobile_location_move}
    />
  );
};

const EventDetails = ({ event, onOpenAsset }) => {
  if (event.eventType === "asset_entered" || event.eventType === "asset_left") {
    const verb = event.eventType === "asset_entered" ? "stored here" : "removed from here";
    return (
      <>
        Item <ItemLink event={event} onOpenAsset={onOpenAsset} /> {verb}
        {event.subQty != null ? ` · Quantity: ${event.subQty}` : ""}
      </>
    );
  }

  const from = event.fromLocation?.locationName || "—";
  const to = event.toLocation?.locationName || "—";
  const fromWarehouse = event.fromLocation?.warehouse?.name;
  const toWarehouse = event.toLocation?.warehouse?.name;
  const inter = crossesWarehouses(event);
  const count = movedItemsCount(event);
  const showWarehouseLine = inter && fromWarehouse && toWarehouse;

  return (
    <Box display="flex" flexDirection="column" gap={0.25} alignItems="center">
      <Box>
        {inter ? "Transferred" : "Moved"} from <strong>{from}</strong> to <strong>{to}</strong>
      </Box>
      {showWarehouseLine && (
        <Box>
          ({fromWarehouse} to {toWarehouse})
        </Box>
      )}
      {count > 0 && (
        <Box>
          {count} item{count === 1 ? "" : "s"} moved with it.
        </Box>
      )}
    </Box>
  );
};

const EventDate = ({ value }) => {
  const formatted = formatDate(value);

  return (
    <>
      <Box>{formatted.date}</Box>
      {formatted.time && <Box>{formatted.time}</Box>}
    </>
  );
};

const ItemLink = ({ event, onOpenAsset }) => {
  const itemId = event.itemId || event.asset?.itemId;
  const assetId = event.asset?.id || event.assetId;

  if (!itemId) return "—";
  if (!assetId || !onOpenAsset) return itemId;

  return (
    <Box
      component="span"
      sx={{ cursor: "pointer", display: "inline" }}
      color="primary.main"
      className="unfocus"
      onClick={() => onOpenAsset(assetId)}
    >
      {itemId}
    </Box>
  );
};

export default function LocationActivity({ subjectType, subjectId, onOpenAsset }) {
  const navigate = useNavigate();
  const location = useLocation();
  const sectionRef = useRef(null);
  const hasScrolledFromRouteRef = useRef(false);
  const activityRequested =
    new URLSearchParams(location.search).get(LOCATION_ACTIVITY_PARAM) === "true";
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState(activityRequested);

  const { data: { locationTimeline: { collection = [], metadata = {} } = {} } = {}, loading } =
    useQuery(LOCATION_TIMELINE_QUERY, {
      skip: !expanded || !subjectId || !subjectType,
      fetchPolicy: "network-only",
      nextFetchPolicy: "cache-first",
      variables: {
        subjectType,
        subjectId,
        page,
        limit: PAGE_LIMIT,
      },
    });

  const totalPages = metadata?.totalPages || 1;

  useEffect(() => {
    setExpanded(activityRequested);
    if (!activityRequested) {
      hasScrolledFromRouteRef.current = false;
    }
  }, [activityRequested]);

  useEffect(() => {
    if (!expanded || !activityRequested || loading || hasScrolledFromRouteRef.current) {
      return;
    }

    const scrollToSection = () => {
      sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      hasScrolledFromRouteRef.current = true;
    };

    window.requestAnimationFrame(scrollToSection);
  }, [expanded, activityRequested, loading]);

  const handleAccordionChange = (_event, isExpanded) => {
    setExpanded(isExpanded);

    const nextParams = new URLSearchParams(location.search);
    if (isExpanded) {
      nextParams.set(LOCATION_ACTIVITY_PARAM, "true");
    } else {
      nextParams.delete(LOCATION_ACTIVITY_PARAM);
    }

    navigate({ search: nextParams.toString() }, { replace: true });
  };

  return (
    <Paper ref={sectionRef} sx={{ p: 2 }}>
      <Accordion
        disableGutters
        expanded={expanded}
        onChange={handleAccordionChange}
        sx={{ boxShadow: "none", "&:before": { display: "none" } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
          <Box>
            <Typography fontWeight={500} fontSize={20}>
              Location Activity
            </Typography>
            <Typography color="text.secondary" fontSize={14}>
              Expand this section to view the detailed history within this location.
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 0, pb: 0 }}>
          {loading ? (
            <Box mt={2}>
              <LinearProgress />
            </Box>
          ) : collection.length === 0 ? (
            <Box
              mt={1}
              p={3}
              bgcolor="#f4f6f8"
              borderRadius="4px"
              display="flex"
              justifyContent="center"
            >
              No recorded activity for this location yet
            </Box>
          ) : (
            <>
              <Table aria-label="Location Activity Table" size="small">
                <TableHead>
                  <TableRow>
                    <TableCell align="center">Date</TableCell>
                    <TableCell align="center">Action</TableCell>
                    <TableCell align="center">Performed By</TableCell>
                    <TableCell align="center">Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {collection.map((event) => (
                    <TableRow key={event.id}>
                      <TableCell align="center">
                        <EventDate value={event.performedAt} />
                      </TableCell>
                      <TableCell align="center">
                        <EventLabel event={event} />
                      </TableCell>
                      <TableCell align="center">{event.user?.email || "System"}</TableCell>
                      <TableCell align="center">
                        <EventDetails event={event} onOpenAsset={onOpenAsset} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {totalPages > 1 && (
                <Box display="flex" justifyContent="center" mt={2}>
                  <Pagination
                    size="medium"
                    color="primary"
                    count={totalPages}
                    page={page}
                    onChange={(_event, value) => setPage(value)}
                  />
                </Box>
              )}
            </>
          )}
        </AccordionDetails>
      </Accordion>
    </Paper>
  );
}
